import {
  Button,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import PhoneInput from 'components/Hero/PhoneInput'
import SwitchInput from 'components/Hero/SwitchInput'
import { memo, useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { $fetch, useFetch } from 'utils/fetch'
import { queryClient } from 'utils/queryClient'

const DEFAULT_FORM_VALUES = {
  phone_number: '',
  inbound: false,
  outbound: false,
  sip_endpoint: '',
}

function ExistingNumberModal({ isOpen, onOpenChange, numberToEdit }) {
  const { handleSubmit, control, setValue, reset, register } = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  })
  const inbound = useWatch({ control, name: 'inbound' })
  const outbound = useWatch({ control, name: 'outbound' })

  const { data: sipConfigs, isFetching } = useFetch(`/v1/sip?inbound_id=${numberToEdit?.id}`, {
    queryKey: ['sipConfigs', numberToEdit?.id],
    enabled: !!numberToEdit?.id,
    cacheTime: 0,
    staleTime: 0,
    refetchOnMount: true,
  })

  useEffect(() => {
    if (numberToEdit?.phone_number) {
      setValue('phone_number', numberToEdit?.phone_number)
    }

    const inboundConfig = sipConfigs?.find(config => config.direction === 'inbound')
    const outboundConfig = sipConfigs?.find(config => config.direction === 'outbound')

    setValue('inbound', !!inboundConfig)
    setValue('outbound', !!outboundConfig)
    setValue('sip_endpoint', outboundConfig?.sip_endpoint || '')
  }, [sipConfigs, setValue, numberToEdit?.phone_number])

  const cleanUp = () => {
    queryClient.invalidateQueries(['inboundNumbers', 'sipConfigs'])
    reset()
    onOpenChange(false)
    toast.success(`Successfully ${numberToEdit ? 'updated' : 'added'} phone number`, { id: 'sip-toast' })
  }

  const { mutateAsync: detachSipConfig, isPending: isDetaching } = useMutation({
    mutationKey: ['detachSipConfig'],
    mutationFn: async ({ phone_number, directions }) => {
      if (!numberToEdit || !sipConfigs) {
        toast.error('No phone number or SIP config found')
        return
      }

      const response = await $fetch('/v1/sip/detach', {
        ignoreResponseError: true,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          service: 'sip',
          phone_number,
          directions,
        },
      })

      if (response.errors) {
        toast.error(response.errors?.[0]?.message || 'An error occurred while adding the phone number')
        return
      }
      cleanUp()
    },
  })

  const { mutateAsync: updateSipConfig, isPending: isUpdating } = useMutation({
    mutationKey: ['updateSipConfig'],
    mutationFn: async ({ phone_number, sip_endpoint }) => {
      if (!numberToEdit || !sipConfigs) {
        toast.error('No phone number or SIP config found')
        return
      }

      const response = await $fetch('/v1/sip/update', {
        ignoreResponseError: true,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          phone_number,
          updates: {
            type: 'outbound',
            sip_endpoint,
          },
        },
      })

      if (response.errors) {
        toast.error(response.errors?.[0]?.message || 'An error occurred while updating SIP configs')
        return
      }
      cleanUp()
    },
  })

  const { mutateAsync: attachSipConfig, isPending: isAttaching } = useMutation({
    mutationKey: ['attachSipConfig'],
    mutationFn: async ({ phone_number, directions }) => {
      const response = await $fetch('/v1/sip/attach', {
        ignoreResponseError: true,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: {
          phone_number,
          directions,
          service: 'sip',
        },
      })

      if (response.errors) {
        toast.error(response.errors?.[0]?.message || 'An error occurred while adding the phone number')
        return
      }
      cleanUp()
    },
  })

  const onSubmit = async (data) => {
    const isNewNumber = !numberToEdit
    if (isNewNumber) {
      const directions = [
        ...(inbound ? [{ type: 'inbound' }] : []),
        ...(outbound ? [{ type: 'outbound', sip_endpoint: data.sip_endpoint }] : []),
      ]
      await attachSipConfig({ sip_endpoint: data.sip_endpoint, directions, phone_number: data.phone_number })
    }
    else {
      const deletedOutbound = !outbound ? sipConfigs.filter(config => config.direction === 'outbound') : []
      const deletedInbound = !inbound ? sipConfigs.filter(config => config.direction === 'inbound') : []

      const deletedDirections = [...deletedInbound, ...deletedOutbound].map(direction => ({
        type: direction.direction,
        sip_endpoint: direction.sip_endpoint ? direction.sip_endpoint : undefined,
      }))

      if (deletedDirections.length > 0) {
        // Detach SIP config before updating since the number may change after `updateSipConfig`
        await detachSipConfig({ directions: deletedDirections, phone_number: numberToEdit.phone_number })
      }

      const previousSipEndpoint = sipConfigs.find(config => config.direction === 'outbound')?.sip_endpoint
      const updatedOutbound = outbound && !!previousSipEndpoint && data.sip_endpoint?.trim() !== previousSipEndpoint.trim()
      if (updatedOutbound) {
        await updateSipConfig({ sip_endpoint: data.sip_endpoint, phone_number: numberToEdit.phone_number })
      }

      const newInbound = inbound && !sipConfigs.find(config => config.direction === 'inbound')
      const newOutbound = outbound && !sipConfigs.find(config => config.direction === 'outbound')
      const directionsToAttach = [
        ...(newInbound ? [{ type: 'inbound' }] : []),
        ...(newOutbound ? [{ type: 'outbound', sip_endpoint: data.sip_endpoint }] : []),
      ]

      if (directionsToAttach.length > 0) {
        await attachSipConfig({ sip_endpoint: data.sip_endpoint, directions: directionsToAttach, phone_number: numberToEdit.phone_number })
      }
    }
  }

  const isFormValid = inbound || outbound
  const isDeleting = numberToEdit && !isFormValid
  const isMutating = isAttaching || isUpdating || isDetaching

  return (
    <Modal
      radius="sm"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      classNames={{
        footer: 'ml-auto',
        body: 'flex flex-col gap-5 w-full pt-4',
        header: 'border-b border-gray-200 bg-[#18181b] w-full',
      }}
    >
      <ModalContent>
        {onClose => (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-2xl font-taurus text-white font-semibold flex items-center gap-3">
                {`${numberToEdit ? 'Edit' : 'Add'} existing number`}
              </h2>
              <p className="text-xs font-taurus font-light tracking-wider text-foreground-400">{`${numberToEdit ? 'Edit' : 'Add'} your existing phone number via SIP`}</p>
            </ModalHeader>

            {isFetching
              ? (
                  <div
                    className="flex justify-center items-center h-60 w-full"
                  >
                    <Spinner />
                  </div>
                )
              : (
                  <>
                    <ModalBody className="flex-start flex flex-col">
                      {numberToEdit
                        ? (
                            <Input
                              label="Phone number"
                              isDisabled
                              name="phone_number"
                              variant="bordered"
                              radius="sm"
                              labelPlacement="outside"
                              value={numberToEdit?.phone_number}
                            />
                          )
                        : (
                            <PhoneInput
                              isRequired
                              defaultValue={numberToEdit?.phone_number}
                              onChange={value => setValue('phone_number', value)}
                            />

                          )}

                      <SwitchInput
                        isSelected={inbound}
                        label="Inbound"
                        className="justify-start"
                        onValueChange={(isSelected) => { setValue('inbound', isSelected) }}
                        size="sm"
                      >
                      </SwitchInput>

                      <SwitchInput
                        isSelected={outbound}
                        label="Outbound"
                        className="justify-start"
                        onValueChange={isSelected => setValue('outbound', isSelected)}
                        size="sm"
                      />

                      {outbound && (
                        <div className="flex flex-col gap-5 p-4 pb-5 border border-gray-200 rounded-md shadow-sm">
                          <h2 className="text-base">Outbound settings</h2>
                          <Input
                            isRequired
                            {...register('sip_endpoint')}
                            label="SIP Endpoint"
                            variant="bordered"
                            radius="sm"
                            labelPlacement="outside"
                            placeholder="e.g. sip.example.com"
                          />
                        </div>
                      )}

                    </ModalBody>
                    <ModalFooter>
                      <Button color="default" radius="sm" variant="light" onPress={onClose}>
                        Close
                      </Button>
                      <Button isLoading={isMutating} color={isDeleting ? 'danger' : 'primary'} radius="sm" type="submit" isDisabled={!isFormValid && !numberToEdit}>
                        {isDeleting ? 'Delete' : 'Save'}
                      </Button>
                    </ModalFooter>
                  </>
                )}
          </Form>
        )}
      </ModalContent>
    </Modal>
  )
}

export default memo(ExistingNumberModal)
